import { Injectable } from '@angular/core';
import { catchError, defer, forkJoin, map, of, Subject, take, type Observable } from 'rxjs';

import type { Entry } from '../../../features/admin/modules/modules.model';

type ReviewFlush = () => Observable<boolean>;

/** Connects a review action bar to the currently mounted review field without owning UI state. */
@Injectable({ providedIn: 'root' })
export class ReviewWriteRegistry {
  readonly #flushByEntryId = new Map<string, Set<ReviewFlush>>();
  readonly #entryUpdates = new Subject<Entry>();
  readonly entryUpdates = this.#entryUpdates.asObservable();

  register(entryId: string, flush: ReviewFlush): () => void {
    const writers = this.#flushByEntryId.get(entryId) ?? new Set<ReviewFlush>();
    writers.add(flush);
    this.#flushByEntryId.set(entryId, writers);
    return () => {
      const registered = this.#flushByEntryId.get(entryId);
      registered?.delete(flush);
      if (registered?.size === 0) this.#flushByEntryId.delete(entryId);
    };
  }

  publishEntry(entry: Entry): void {
    this.#entryUpdates.next(entry);
  }

  flush(entryId: string | undefined): Observable<boolean> {
    if (!entryId) return of(true);
    const writers = [...(this.#flushByEntryId.get(entryId) ?? [])];
    if (writers.length === 0) return of(true);
    return forkJoin(
      writers.map(flush =>
        defer(flush).pipe(
          take(1),
          catchError(() => of(false))
        )
      )
    ).pipe(map(results => results.every(Boolean)));
  }
}
